/**
 * JWT payload decoder (task 7.4, public-website spec).
 *
 * Pure function that extracts the **payload** segment of a JSON Web
 * Token (RFC 7519) — `header.payload.signature` — base64url-decodes
 * it, JSON-parses it, and returns the resulting object.
 *
 * **This module does NOT verify the JWT's signature.** Per
 * **design §2.3 (Sign-in flow — federated OAuth via Cognito user
 * pool)** and **§4.3 (Authentication on the public REST API
 * gateway)**, signature verification is the **API Gateway Cognito
 * authorizer's** job — the gateway rejects forged or tampered
 * tokens before any backend Lambda runs. The SPA's only use of the
 * decoded payload is to populate UI affordances (the `sub` claim
 * keys profile lookups, the `exp` claim drives the "is the token
 * still valid?" check, the `email` and `name` claims feed the
 * AppShell's signed-in chrome). A forged token that decodes to
 * plausible-looking claims still fails authorization at the
 * gateway, so SPA-side signature verification would be redundant
 * and would require shipping the user pool's JWKS to the browser.
 *
 * Function-length budget: every export below stays under 20 lines
 * per Requirement 3.7 (mirrors the discipline established in
 * `lib/auth/pkce.ts` + `lib/auth/token-storage.ts`).
 */

/**
 * Common Cognito ID token claims surfaced by the SPA.
 *
 * The named fields below are the claims the SPA's auth slice +
 * AppShell + RequireAuth guard read directly. The
 * `[claim: string]: unknown` index signature keeps the type open
 * for additional Cognito-specific claims (`cognito:username`,
 * `cognito:groups`, `identities`, `token_use`, `auth_time`,
 * `email_verified`, `picture`, `phone_number`, custom attributes
 * prefixed `custom:`) without forcing every consumer to widen the
 * type.
 *
 * Field semantics:
 *
 * - `sub` — the Cognito user pool's stable user identifier (UUID
 *   v4). The only claim guaranteed to be present on every Cognito
 *   ID token; the SPA uses it as the partition-key value when
 *   reading / writing rows in
 *   `dvgupv2-profile-table-{region}-{stage}` (Requirement 6.2).
 * - `email` / `name` — populated for federated identities (Login
 *   with Amazon, Login with Google) once the user has consented to
 *   share them. Optional because Cognito will omit a claim that
 *   the IdP did not return.
 * - `exp` — expiration epoch **seconds** (RFC 7519 §4.1.4 NumericDate).
 *   The auth slice converts to milliseconds (`exp * 1000`) before
 *   comparing against `Date.now()`.
 * - `iat` — "issued at" epoch seconds; optional in this type but
 *   always present on Cognito-issued tokens.
 * - `iss` — token issuer URL, e.g.
 *   `https://cognito-idp.us-east-1.amazonaws.com/{userPoolId}`.
 * - `aud` — token audience; the Cognito SPA app client id. Should
 *   match `PublicCognitoUserPoolClientId` from `/config.json`.
 */
export interface JwtPayload {
  /** Cognito user pool's stable user identifier. */
  sub: string;
  /** End-user email address (when the federated IdP shared it). */
  email?: string;
  /** End-user display name (when the federated IdP shared it). */
  name?: string;
  /** Expiration epoch seconds (RFC 7519 §4.1.4 NumericDate). */
  exp: number;
  /** Issued-at epoch seconds (RFC 7519 §4.1.6). */
  iat?: number;
  /** Token issuer URL (RFC 7519 §4.1.1). */
  iss?: string;
  /** Token audience — the Cognito SPA app client id (RFC 7519 §4.1.3). */
  aud?: string;
  /** Open for any additional Cognito or IdP-specific claim. */
  [claim: string]: unknown;
}

/**
 * Restore base64 padding (`=`) stripped during base64url encoding.
 *
 * RFC 4648 §5 base64url encoding strips the trailing `=` padding;
 * `atob` (and Node's `Buffer.from(..., 'base64')`) require length
 * to be a multiple of 4. The pad count is `(4 - len % 4) % 4`.
 *
 * Internal helper kept private so the public surface is a single
 * function.
 */
function padBase64(input: string): string {
  const remainder = input.length % 4;
  return remainder === 0 ? input : input + '='.repeat(4 - remainder);
}

/**
 * Base64url-decode the supplied string into a UTF-8 string.
 *
 * Substitutes the URL-safe alphabet (`-` → `+`, `_` → `/`) before
 * delegating to `atob`. Throws if the input contains any character
 * outside the base64 alphabet — the caller ({@link decodeJwt})
 * catches the throw and returns `null`.
 *
 * Internal helper kept private to keep the public surface limited
 * to {@link decodeJwt}.
 */
function base64UrlDecodeToString(input: string): string {
  const standard = input.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padBase64(standard));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new TextDecoder().decode(bytes);
}

/**
 * Decode the **payload** segment of a JWT into a typed object.
 *
 * Splits the supplied token on `.`, takes the middle segment
 * (RFC 7519 §3 — `header.payload.signature`), base64url-decodes it,
 * and `JSON.parse`s the resulting UTF-8 string. Returns `null` on
 * any failure (wrong segment count, invalid base64url, invalid
 * JSON) so callers can simply branch on a falsy return.
 *
 * **Does NOT verify the signature.** Per **design §2.3 / §4.3**,
 * signature verification is the API Gateway Cognito authorizer's
 * job. See the module-level TSDoc for the rationale.
 *
 * The optional generic `T` lets callers narrow the return type to
 * a specific claim shape (e.g. an extended interface that pins
 * down a `custom:plan` string). Defaults to {@link JwtPayload}.
 *
 * @param token The JWT compact serialization
 *   `header.payload.signature` as issued by the Cognito Hosted UI's
 *   `/oauth2/token` endpoint.
 * @returns The parsed payload as `T`, or `null` if any decoding
 *   step failed.
 */
export function decodeJwt<T = JwtPayload>(token: string): T | null {
  const segments = token.split('.');
  if (segments.length !== 3) return null;
  try {
    const json = base64UrlDecodeToString(segments[1]);
    return JSON.parse(json) as T;
  } catch {
    return null;
  }
}
